import { transformToLocaleObject } from './utils';

type LocaleMessages = Record<string, string>;

export const SUPPORTED_LANGUAGES = {
  en: 'en',
  es: 'es',
  fr: 'fr_FR',
  de: 'de_DE',
  ja: 'ja_JP',
  'fr-CA': 'fr_CA',
  'pt-BR': 'pt_BR',
  'zh-CN': 'zh_CN',
  'zh-TW': 'zh_TW',
  'it-IT': 'it_IT',
  'nl-NL': 'nl_NL',
  'ko-KR': 'ko_KR',
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

const localeLoaders = Object.fromEntries(
  Object.entries(SUPPORTED_LANGUAGES).map(([key, name]) => [
    key,
    () => import(`./locales/${name}.json`).then((module) => module.default),
  ]),
) as Record<
  SupportedLanguage,
  () => Promise<Record<SupportedLanguage, { value: string }>>
>;

const cache = new Map<SupportedLanguage, LocaleMessages>();
(async () => {
  const messages = await localeLoaders['en']();
  const transformed = transformToLocaleObject(messages);
  cache.set('en', transformed);
  return transformed;
})();

export const loadLocale = async (
  language: SupportedLanguage,
): Promise<LocaleMessages> => {
  const cached = cache.get(language);
  if (cached) {
    return cached;
  }

  const loader = localeLoaders[language];
  if (!loader) {
    return cache.get('en')!;
  }

  const transformed = transformToLocaleObject(await loader());
  cache.set(language, transformed);
  return transformed;
};

export const getEnglishMessages = (): LocaleMessages => cache.get('en') ?? {};

export const getMessages = (language: SupportedLanguage): LocaleMessages =>
  cache.get(language) ?? getEnglishMessages();

export const isLocaleLoaded = (language: SupportedLanguage): boolean =>
  cache.has(language);
